import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

export interface Post {
  id: string;
  content: string;
  user_id: string | null;
  topic_id: string;
  parent_post_id: string | null;
  created_at: string;
  updated_at: string;
  profiles?: {
    display_name: string;
    avatar_url: string | null;
  };
}

interface UsePostsOptions {
  page?: number;
  limit?: number;
  totalCount?: number;
}

interface UsePostsResult {
  posts: Post[];
  totalCount: number;
}

export const usePosts = (topicId: string, options: UsePostsOptions = {}) => {
  const { page = 1, limit = 20, totalCount } = options;
  const offset = (page - 1) * limit;

  return useQuery({
    queryKey: ['posts', topicId, page, limit],
    queryFn: async (): Promise<UsePostsResult> => {
      // Keep this cheap: fetch visible posts only. Topic.reply_count is the cached count.
      const { data: posts, error } = await supabase
        .from('posts')
        .select('id, content, user_id, topic_id, parent_post_id, created_at, updated_at')
        .eq('topic_id', topicId)
        .order('created_at', { ascending: true })
        .range(offset, offset + limit - 1);

      if (error) throw error;

      const enrichedPosts = posts?.map(post => ({
        ...post,
        profiles: null,
      })) || [];

      return {
        posts: enrichedPosts,
        totalCount: totalCount ?? enrichedPosts.length,
      };
    },
    enabled: !!topicId,
    staleTime: 60_000,
  });
};
